/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/43bbe9d/screen/52533796/layer/81006795
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import find from 'ramda/src/find';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import propEq from 'ramda/src/propEq';
import moment from 'moment';
import Avatar from '@components/@core/Avatar';
import Button from '@components/@core/AnimatedButton';
import DatePicker from '@components/@core/DatePicker';
import Dropdown from '@components/@core/Dropdown';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Input from '@components/@core/Input';
import AppActions from '~/actions/app';
import CompanyActions from '~/actions/company';
import DevelopActions from '~/actions/develop';
import { UserType } from '~/services/config';
import { validateEmail } from '~/services/util';

class AddEmployee extends Component {
    state = {
        page: 0,
        disabled: true,
        emailError: '',
        otherEmailError: '',
        companyId: this.props.companyId,
        createdUser: null,
        thumbnail: '/images/default.png',
        firstName: '',
        lastName: '',
        cellPhone: '',
        officePhone: '',
        workEmail: '',
        otherEmail: '',
        aboutMe: '',
        careerGoal: '',
        hireDate: moment(),
        appRole: UserType[3],
        jobRole: null,
        department: null,
        manager: null,
        team: null,
        payPlan: null,
        supervisor: null,
        directReports: null,
        careerProgram: null,
        scorecards: null,
        badges: null,
        habitSchedule: null,
        certifications: null,
        hasAssigned: false,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { firstName, lastName, jobRole, appRole, department, emailError, otherEmailError } = prevState;
        const disabled = isEmpty(firstName) || isEmpty(lastName) || isNil(jobRole) || isNil(appRole) || isNil(department) || !isEmpty(emailError) || !isEmpty(otherEmailError);
        return { disabled };
    }

    componentDidMount() {
        this.props.getCompanyEmployees(this.state.companyId, false);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.companyId !== prevState.companyId) {
            this.props.getCompanyEmployees(this.state.companyId, false);
        }
    }

    handleFilter = (type, data) => {
        if (data[0].id < 0) this.setState({ companyId: this.props.companyId });
        else this.setState({ companyId: data[0].id });
    };

    handleEmailValidate = (type) => (e) => {
        if (!validateEmail(e)) {
            if (type === 'work') this.setState({ emailError: 'The email is invalid. Please input a valid email!' });
            if (type === 'other') this.setState({ otherEmailError: e !== '' ? 'The email is invalid. Please input a valid email!' : '' });
        } else {
            if (type === 'other') this.setState({ otherEmailError: '' });
            if (type === 'work') {
                const { companyId, workEmail } = this.state;
                const payload = { companyId, search: workEmail };
                this.props.emailVerify(payload, (res) => {
                    if (res.users.length > 0) {
                        this.setState({ emailError: 'Email is already used in. Please try another!' });
                    } else {
                        this.setState({ emailError: '' });
                    }
                });
            }
        }
    };

    handleChangeEmail = (type) => (e) => {
        const { emailError, otherEmailError } = this.state;
        if (type === 'other') {
            this.setState({ otherEmail: e });
            if (otherEmailError !== '' && validateEmail(e)) {
                this.setState({ otherEmailError: '' });
            }
        }
        if (type === 'work') {
            this.setState({ workEmail: e });
            if (emailError !== '' && validateEmail(e)) {
                this.setState({ emailError: '' });
            }
        }
    };

    handlePhoneNumber = (type) => (e) => {
        const validateNumber = e.replace(/[^0-9.]/g, '');
        type === 'Cell' ? this.setState({ cellPhone: validateNumber }) : this.setState({ officePhone: validateNumber });
    };

    handleSubmit = () => {
        const { firstName, lastName, cellPhone, officePhone, workEmail, otherEmail, aboutMe, careerGoal, jobRole, hireDate, appRole, manager } = this.state;

        const payload = {
            new_user: [
                {
                    firstName,
                    lastName,
                    phone: cellPhone,
                    secondaryPhone: officePhone,
                    email: workEmail,
                    email2: otherEmail,
                    aboutMe: aboutMe,
                    careerGoal: careerGoal,
                    managerId: manager.id,
                    appRoleId: appRole.id,
                    jobRoleId: jobRole.id,
                    jobTitleId: jobRole.titles[0].id,
                    birthday: null,
                    biography: null,
                    timezone: null,
                    hiredAt: hireDate.format('YYYY-MM-DD'),
                },
            ],
        };
        this.props.addUsers(payload, this.state.companyId, this.handleNextPage);
    };

    handleNextPage = (res) => {
        this.setState({ createdUser: res.data.users[0].id });
        this.setState({ page: 1 });
        this.props.getFeeds(res.data.users[0].id, 'habitslist', { status: [0, 1, 2], archived: 1 });
        this.props.getUserPrograms(this.state.createdUser);
        this.props.getUserScorecards(this.state.createdUser);
    };

    handleModal = (type) => () => {
        const employees = this.props.employees;
        const { companyId } = this.state;

        if (type === 'career') {
            this.props.toggleModal({
                type: 'Assign Program',
                data: {
                    before: {
                        disabled: ['certifications'],
                        assignees: [this.state.createdUser],
                        companyId: companyId,
                        employees: employees[companyId],
                    },
                },
                callBack: () => this.props.getUserPrograms(this.state.createdUser),
            });
        } else if (type === 'certifications') {
            this.props.toggleModal({
                type: 'Assign Program',
                data: {
                    before: {
                        disabled: ['careers'],
                        assignees: [this.state.createdUser],
                        companyId: companyId,
                        employees: employees[companyId],
                    },
                },
                callBack: () => this.props.getUserPrograms(this.state.createdUser),
            });
        } else if (type === 'scorecard') {
            this.props.toggleModal({
                type: 'Assign ToDo',
                data: {
                    before: {
                        disabled: ['habits', 'habitslist', 'quotas'],
                        assignees: [this.state.createdUser],
                        companyId: companyId,
                        employees: employees[companyId],
                    },
                },
                callBack: () => this.props.getUserScorecards(this.state.createdUser),
            });
        } else {
            this.props.toggleModal({
                type: 'Assign ToDo',
                data: {
                    before: {
                        disabled: ['habits', 'scorecards', 'quotas'],
                        assignees: [this.state.createdUser],
                        companyId: companyId,
                        employees: employees[companyId],
                    },
                },
                callBack: () => this.props.getFeeds(this.state.createdUser, 'habitslist', { status: [0, 1, 2], archived: 1 }),
            });
        }
    };

    handleSkip = () => {
        this.setState({
            page: 0,
            disabled: true,
            thumbnail: '/images/default.png',
            firstName: '',
            lastName: '',
            cellPhone: '',
            officePhone: '',
            workEmail: '',
            otherEmail: '',
            aboutMe: '',
            careerGoal: '',
            hireDate: moment(),
            appRole: UserType[3],
            jobRole: null,
            department: null,
            manager: null,
            team: null,
            payPlan: null,
            supervisor: null,
            directReports: null,
            careerProgram: null,
            scorecards: null,
            badges: null,
            habitSchedule: null,
            certifications: null,
            hasAssigned: false,
        });
    };

    render() {
        const { companies, employees } = this.props;
        const {
            page,
            disabled,
            emailError,
            otherEmailError,
            thumbnail,
            firstName,
            lastName,
            cellPhone,
            officePhone,
            workEmail,
            otherEmail,
            aboutMe,
            careerGoal,
            hireDate,
            appRole,
            companyId,
            createdUser,
        } = this.state;
        const userType = UserType.filter((type) => !(type.label === 'Super Admin'));
        const company = find(propEq('id', companyId), companies);
        const departments = company.departments;
        const managers = company.managers;
        const roles = company.job_roles;

        const employee = createdUser ? find(propEq('id', createdUser), employees[companyId]) : null;
        const habitslist = this.props.habitslist;

        const programs = this.props.programs.data;
        const scorecards = this.props.scorecards.data;
        const career = programs.filter((program) => program.type === 1);
        const certification = programs.filter((program) => program.type === 2);
        return (
            <ErrorBoundary className="mng-add-employee">
                <Filter mountEvent filters={['company']} onChange={this.handleFilter} />
                {page === 0 ? (
                    <div className="card">
                        <div className="permission ms-lg-auto w-80 ms-0">
                            <div className="pb-4 border-bottom">
                                <p className="dsl-b16 bold">Permissions</p>
                                <Dropdown
                                    title="User Type"
                                    data={userType}
                                    width={250}
                                    align="right"
                                    placeholder="User"
                                    returnBy="data"
                                    defaultIds={[appRole.id]}
                                    getValue={(e) => e.label}
                                    onChange={(e) => this.setState({ appRole: e[0] })}
                                />
                            </div>
                        </div>
                        <div className="employee-form d-flex ssm-column pt-4">
                            <Avatar className="m-auto" type="logo" size="extraLarge" url={thumbnail} borderWidth={4} upload onDrop={(e) => this.setState({ thumbnail: e })} />
                            <div className="info ms-sm-4">
                                <div className="py-0">
                                    <p className="dsl-b16 bold">About</p>
                                    <Input title="First name" placeholder="Type here..." value={firstName} onChange={(e) => this.setState({ firstName: e })} />
                                    <Input title="Last name" placeholder="Type here..." value={lastName} onChange={(e) => this.setState({ lastName: e })} />
                                    <Input title="Cell phone" placeholder="Type here..." value={cellPhone} onChange={this.handlePhoneNumber('Cell')} />
                                    <Input title="Office phone" placeholder="Type here..." value={officePhone} onChange={this.handlePhoneNumber('Office')} />
                                    <Input
                                        title="Work email"
                                        placeholder="Type here..."
                                        value={workEmail}
                                        error={emailError}
                                        onChange={this.handleChangeEmail('work')}
                                        onBlur={this.handleEmailValidate('work')}
                                    />
                                    <Input
                                        title="Other email"
                                        placeholder="Type here..."
                                        value={otherEmail}
                                        error={otherEmailError}
                                        onChange={this.handleChangeEmail('other')}
                                        onBlur={this.handleEmailValidate('other')}
                                    />
                                </div>
                                <Input title="About me" placeholder="Type here..." value={aboutMe} as="textarea" rows={2} onChange={(e) => this.setState({ aboutMe: e })} />
                                <Input
                                    className="mb-0 pb-4 border-bottom"
                                    title="3 year career goal"
                                    placeholder="Type here..."
                                    value={careerGoal}
                                    onChange={(e) => this.setState({ careerGoal: e })}
                                />
                                <div className="py-4 border-bottom">
                                    <p className="dsl-b16 bold">Position</p>
                                    <div className="d-flex ssm-column">
                                        <div className="d-flex-1">
                                            <DatePicker
                                                title="Hire date"
                                                value={hireDate}
                                                calendar="day"
                                                append="caret"
                                                format="MMM D, YY"
                                                as="span"
                                                onSelect={(e) => this.setState({ hireDate: e })}
                                            />
                                            <Dropdown
                                                title="Department"
                                                className="add-emp"
                                                data={departments}
                                                align="right"
                                                placeholder="Select"
                                                getValue={(data) => data['name']}
                                                onChange={(e) => this.setState({ department: e })}
                                            />
                                            {/* <Dropdown
                        title="Team"
                        data={[]}
                        width={250}
                        align="right"
                        placeholder="Select"
                        getValue={data => data['name']}
                        onChange={e => this.setState({ team: e })}
                      /> */}
                                            {/* <Dropdown
                        title="Supervisor"
                        data={[]}
                        width={250}
                        align="right"
                        placeholder="Select"
                        getValue={data => data['name']}
                        onChange={e => this.setState({ supervisor: e })}
                      /> */}
                                        </div>
                                        <div className="d-flex-1">
                                            <Dropdown
                                                title="Job role"
                                                className="add-emp"
                                                data={roles}
                                                width={250}
                                                align="right"
                                                placeholder="Select"
                                                returnBy="data"
                                                getValue={(e) => e.name}
                                                onChange={(e) => this.setState({ jobRole: e[0] })}
                                            />
                                            {/* <Dropdown
                        title="Pay plan"
                        data={[]}
                        width={250}
                        align="right"
                        placeholder="Select"
                        getValue={data => data['name']}
                        onChange={e => this.setState({ payPlan: e })}
                      /> */}
                                            {/* <Dropdown
                        title="Direct reports"
                        data={[]}
                        width={250}
                        align="right"
                        placeholder="Select"
                        getValue={e => e.name}
                        onChange={e => this.setState({ directReports: e })}
                      /> */}
                                            <Dropdown
                                                title="Manager"
                                                className="add-emp"
                                                data={managers}
                                                width={250}
                                                align="right"
                                                placeholder="Select"
                                                returnBy="data"
                                                getValue={(e) => `${e.profile.first_name} ${e.profile.last_name}`}
                                                onChange={(e) => this.setState({ manager: e[0] })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex mt-3">
                            <Button className="ml-auto" disabled={disabled} name="Save" onClick={this.handleSubmit} />
                        </div>
                    </div>
                ) : (
                    <div className="card">
                        <p className="dsl-b16 bold">Assignments</p>
                        <div className="d-flex">
                            <div className="d-flex-1">
                                <div className="assign">
                                    <span className="dsl-m12">Health Plan program</span>
                                    <Button type="low" name="Assign" onClick={this.handleModal('career')} />
                                    <p className="dsl-p16 mb-0">{!isEmpty(career) && career[0]?.title}</p>
                                </div>
                                <div className="assign">
                                    <span className="dsl-m12">Certifications</span>
                                    <Button type="low" name="Assign" onClick={this.handleModal('certifications')} />
                                    <p className="dsl-p16 mb-0">{!isEmpty(certification) && certification[0].title}</p>
                                </div>
                            </div>
                            <div className="d-flex-1">
                                <div className="assign">
                                    <span className="dsl-m12">Scorecards</span>
                                    <Button type="low" name="Assign" onClick={this.handleModal('scorecard')} />
                                    <p className="dsl-p16 mb-0">{!isEmpty(scorecards) && scorecards[0]?.title}</p>
                                </div>
                                <div className="assign">
                                    <span className="dsl-m12">Habit schedule</span>
                                    <Button type="low" name="Assign" onClick={this.handleModal('habitschedule')} />
                                    <p className="dsl-p16 mb-0">{!isEmpty(habitslist?.data) && habitslist.data[0].data.name}</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-h-end mt-3">
                            <Button name="Continue" onClick={this.handleSkip} />
                        </div>
                    </div>
                )}
            </ErrorBoundary>
        );
    }
}

AddEmployee.propTypes = {
    userId: PropTypes.number.isRequired,
    companyId: PropTypes.number.isRequired,
    companies: PropTypes.array.isRequired,
    onAdd: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

AddEmployee.defaultProps = {
    userId: 0,
    companyId: 0,
    companies: [],
    onAdd: () => {},
    onClose: () => {},
};

const mapStateToProps = (state) => ({
    userId: state.app.id,
    companyId: state.app.company_info.id,
    companies: state.app.companies,
    employees: state.company.employees,
    habitslist: state.develop.instances.habitslist,
    programs: state.develop.instances.programs,
    scorecards: state.develop.instances.scorecards,
});

const mapDispatchToProps = (dispatch) => ({
    addUsers: (payload, companyId, cb) => dispatch(AppActions.postcompanyusersRequest(payload, companyId, null, cb)),
    emailVerify: (payload, callback) => dispatch(AppActions.emailverificationRequest(payload, callback)),
    getCompanyEmployees: (companyId) => dispatch(CompanyActions.getminimalcompanyemployeesRequest(companyId)),
    getFeeds: (id, target, e, other, s) => dispatch(DevelopActions.getlearnfeedsRequest(id, target, e, other, s)),
    getUserPrograms: (userId) => dispatch(DevelopActions.getuserprogramsRequest(userId)),
    getUserScorecards: (userId) => dispatch(DevelopActions.getuserscorecardsRequest(userId)),
    toggleModal: (payload) => dispatch(AppActions.modalRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddEmployee);
